import { useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  /** 'destructive' renders the confirm button red. */
  variant?: 'default' | 'destructive';
  /** When set, a reason textarea is shown and its value is required. */
  reasonRequired?: boolean;
  /** When set, the user must type this exact string to enable confirm. */
  confirmPhrase?: string;
  /** Disables the confirm button while the action is in flight. */
  pending?: boolean;
  onConfirm: (reason: string) => void;
}

/**
 * Generic confirmation dialog: optional reason textarea + optional
 * type-to-confirm phrase. Built for the action flow; wired in PR-E #2/#3.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  variant = 'default',
  reasonRequired = false,
  confirmPhrase,
  pending = false,
  onConfirm,
}: ConfirmDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');

  const reasonOk = !reasonRequired || reason.trim().length > 0;
  const phraseOk = !confirmPhrase || typed === confirmPhrase;
  const canConfirm = reasonOk && phraseOk && !pending;

  function handleOpenChange(next: boolean): void {
    if (!next) {
      setReason('');
      setTyped('');
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>

        {reasonRequired && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-reason">Reason</Label>
            <Textarea
              id="confirm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you doing this? (recorded in the audit log)"
            />
          </div>
        )}

        {confirmPhrase && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-phrase">
              Type <span className="font-mono font-semibold">{confirmPhrase}</span> to confirm
            </Label>
            <Input
              id="confirm-phrase"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            disabled={!canConfirm}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
